import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { knowledgeBaseAPI, documentAPI } from '../services/api';
import { KnowledgeBase as KnowledgeBaseType } from '@shared/types';
import PDFUpload from '../components/upload/PDFUpload';
import ChatInterface from '../components/chat/ChatInterface';
import { AdvancedPDFViewer } from '../components/pdf-viewer';
import './KnowledgeBase.css';

// 临时定义文档记录类型
interface DocumentRecord {
    id: string;
    knowledge_base_id: string;
    filename: string;
    file_size: number;
    file_type: string;
    status: string;
    processing_progress: number;
    created_at: string;
}

const KnowledgeBasePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBaseType | null>(null);
    const [documents, setDocuments] = useState<DocumentRecord[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [showUpload, setShowUpload] = useState<boolean>(false);
    const [showChat, setShowChat] = useState<boolean>(false);
    const [viewingDocument, setViewingDocument] = useState<DocumentRecord | null>(null);

    // 加载知识库信息
    const loadKnowledgeBase = useCallback(async () => {
        if (!id) return;

        try {
            setLoading(true);
            // 暂时从所有知识库中查找，后续实现getKnowledgeBase API
            const result = await knowledgeBaseAPI.getKnowledgeBases();
            if (result.success && result.data) {
                const kb = result.data.find(k => k.id === id);
                if (kb) {
                    setKnowledgeBase(kb);
                } else {
                    setError('知识库不存在');
                }
            } else {
                setError('知识库不存在');
            }
        } catch (error) {
            console.error('加载知识库失败:', error);
            setError('加载知识库失败，请稍后再试');
        } finally {
            setLoading(false);
        }
    }, [id]);

    // 加载文档列表
    const loadDocuments = useCallback(async () => {
        if (!id) return;

        try {
            const result = await documentAPI.getKnowledgeBaseDocuments(id);
            if (result.success && result.documents) {
                setDocuments(result.documents);
            }
        } catch (error) {
            console.error('加载文档列表失败:', error);
        }
    }, [id]);

    useEffect(() => {
        loadKnowledgeBase();
        loadDocuments();
    }, [loadKnowledgeBase, loadDocuments]);

    // 查看文档
    const handleViewDocument = (document: DocumentRecord) => {
        setViewingDocument(document);
    };

    // 关闭文档查看器
    const handleCloseViewer = () => {
        setViewingDocument(null);
    };

    // 删除文档
    const handleDeleteDocument = async (documentId: string) => {
        if (!confirm('确定要删除这个文档吗？')) {
            return;
        }

        try {
            const result = await documentAPI.deleteDocument(documentId);
            if (result.success) {
                // 重新加载文档列表
                loadDocuments();
            } else {
                setError(result.error || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            setError('删除失败，请稍后再试');
        }
    };

    // 格式化文件大小
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // 格式化日期
    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleString('zh-CN');
    };

    // 获取状态文本
    const getStatusText = (status: string): string => {
        const statusMap: { [key: string]: string } = {
            'pending': '等待处理',
            'uploaded': '已上传',
            'processing': '处理中',
            'completed': '已完成',
            'failed': '处理失败'
        };
        return statusMap[status] || status;
    };

    // 获取文档图标
    const getDocumentIcon = (status: string): string => {
        const iconMap: { [key: string]: string } = {
            'pending': '⏳',
            'uploaded': '📄',
            'processing': '🔄',
            'completed': '✅',
            'failed': '❌'
        };
        return iconMap[status] || '📄';
    };

    if (loading) {
        return (
            <div className="knowledge-base-container">
                <div className="loading-section">
                    <div className="loading-spinner">🔄</div>
                    <p>正在加载知识库...</p>
                </div>
            </div>
        );
    }

    if (error || !knowledgeBase) {
        return (
            <div className="knowledge-base-container">
                <div className="error-section">
                    <div className="error-icon">❌</div>
                    <h2>加载失败</h2>
                    <p>{error || '知识库不存在'}</p>
                    <button onClick={() => navigate('/')} className="back-btn">
                        返回首页
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="knowledge-base-container">
            {/* 页面头部 */}
            <div className="page-header">
                <button onClick={() => navigate('/')} className="back-btn">
                    ← 返回首页
                </button>
                <div className="header-content">
                    <h1>📚 {knowledgeBase.name}</h1>
                    <p className="kb-subtitle">{knowledgeBase.subject} - {knowledgeBase.category}</p>
                    <p className="kb-description">{knowledgeBase.description}</p>
                </div>
                <div className="header-actions">
                    <button
                        onClick={() => setShowChat(!showChat)}
                        className="toggle-chat-btn"
                    >
                        {showChat ? '📋 隐藏问答' : '🤖 智能问答'}
                    </button>
                    <button
                        onClick={() => setShowUpload(!showUpload)}
                        className="toggle-upload-btn"
                    >
                        {showUpload ? '📋 隐藏上传' : '📤 上传文档'}
                    </button>
                </div>
            </div>

            {/* 知识库统计 */}
            <div className="kb-stats-section">
                <div className="stat-item">
                    <div className="stat-icon">📄</div>
                    <div className="stat-content">
                        <div className="stat-number">{documents.length}</div>
                        <div className="stat-label">文档总数</div>
                    </div>
                </div>
                <div className="stat-item">
                    <div className="stat-icon">📊</div>
                    <div className="stat-content">
                        <div className="stat-number">
                            {knowledgeBase.difficulty_level === 'beginner' ? '初级' :
                                knowledgeBase.difficulty_level === 'intermediate' ? '中级' : '高级'}
                        </div>
                        <div className="stat-label">难度等级</div>
                    </div>
                </div>
                <div className="stat-item">
                    <div className="stat-icon">💾</div>
                    <div className="stat-content">
                        <div className="stat-number">
                            {formatFileSize(documents.reduce((sum, doc) => sum + doc.file_size, 0))}
                        </div>
                        <div className="stat-label">总大小</div>
                    </div>
                </div>
                <div className="stat-item">
                    <div className="stat-icon">🔄</div>
                    <div className="stat-content">
                        <div className="stat-number">
                            {knowledgeBase.status === 'active' ? '活跃' : '暂停'}
                        </div>
                        <div className="stat-label">状态</div>
                    </div>
                </div>
            </div>

            {/* 智能问答区域 */}
            {showChat && (
                <div className="chat-section">
                    <ChatInterface
                        knowledgeBaseId={knowledgeBase.id}
                        knowledgeBaseName={knowledgeBase.name}
                    />
                </div>
            )}

            {/* 上传区域 */}
            {showUpload && (
                <div className="upload-section">
                    <h3>📤 上传文档到此知识库</h3>
                    <PDFUpload />
                </div>
            )}

            {/* 文档列表 */}
            <div className="documents-section">
                <div className="section-header">
                    <h3>📚 文档列表</h3>
                </div>

                {documents.length === 0 ? (
                    <div className="no-documents">
                        <div className="no-documents-icon">📭</div>
                        <h3>暂无文档</h3>
                        <p>该知识库还没有任何文档</p>
                        <button
                            onClick={() => setShowUpload(true)}
                            className="upload-first-btn"
                        >
                            🚀 上传第一个文档
                        </button>
                    </div>
                ) : (
                    <div className="documents-list">
                        {documents.map((doc) => (
                            <div key={doc.id} className="document-item">
                                <div className="document-icon">
                                    {getDocumentIcon(doc.status)}
                                </div>
                                <div className="document-info">
                                    <div className="document-name">{doc.filename}</div>
                                    <div className="document-meta">
                                        <span className="meta-item">
                                            📏 大小: {formatFileSize(doc.file_size)}
                                        </span>
                                        <span className={`meta-item status-${doc.status}`}>
                                            🔄 状态: {getStatusText(doc.status)}
                                        </span>
                                        {doc.processing_progress > 0 && (
                                            <span className="meta-item">
                                                📈 进度: {doc.processing_progress}%
                                            </span>
                                        )}
                                    </div>
                                    <div className="document-date">
                                        📅 创建时间: {formatDate(doc.created_at)}
                                    </div>
                                </div>
                                <div className="document-actions">
                                    <button
                                        onClick={() => handleViewDocument(doc)}
                                        className="action-btn view-btn"
                                        title="查看PDF文档"
                                    >
                                        👁️ 查看
                                    </button>
                                    <button
                                        onClick={() => handleDeleteDocument(doc.id)}
                                        className="action-btn delete-btn"
                                        title="删除文档"
                                    >
                                        🗑️ 删除
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* PDF查看器 */}
            {viewingDocument && (
                <AdvancedPDFViewer
                    documentId={viewingDocument.id}
                    filename={viewingDocument.filename}
                    onClose={handleCloseViewer}
                />
            )}
        </div>
    );
};

export default KnowledgeBasePage;
